import { useState } from 'react';
import { toast } from 'sonner';

import {
  type Skill,
  type SkillForm,
  SKILL_WRITE_PROBLEM_FIELD,
  SkillWriteError,
  useDeleteSkillMutation,
  usePutSkillMutation,
  useRefineSkillMutation,
  validateSkillForm,
} from './plugins-query';

export type SkillField = keyof SkillForm;

interface UseSkillFormArgs {
  initial: SkillForm;
  isTruncated: boolean;
  skill?: Skill;
  /** The existing skill a typed name would overwrite or rename onto */
  findTaken?: (name: string) => Skill | undefined;
  onSaved?: (dir: string) => void;
}

export function useSkillForm({
  initial,
  isTruncated,
  skill,
  findTaken,
  onSaved,
}: UseSkillFormArgs) {
  const [form, setForm] = useState(initial);
  const [serverErrors, setServerErrors] = useState<
    Partial<Record<SkillField, string>>
  >({});
  // Bumped when the body is replaced from outside the editor (refine), so the editor reloads it
  const [bodyVersion, setBodyVersion] = useState(0);
  const putSkill = usePutSkillMutation();
  const deleteSkill = useDeleteSkillMutation();
  const refineSkill = useRefineSkillMutation();

  const clientErrors = validateSkillForm(form, findTaken?.(form.name));
  // A truncated file only holds the head of the skill, so saving it would drop the tail
  const canSave =
    Object.keys(clientErrors).length === 0 &&
    !!form.name &&
    !isTruncated &&
    !putSkill.isPending &&
    !refineSkill.isPending;
  // Refining an empty draft has nothing to work from
  const canRefine =
    !!form.name &&
    !!form.description.trim() &&
    !!form.body.trim() &&
    !refineSkill.isPending &&
    !putSkill.isPending;

  const setField = (field: SkillField, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setServerErrors((current) => ({ ...current, [field]: undefined }));
  };

  // Only nag about a field the user has put something in; an empty one is unfinished, not wrong
  const errorFor = (field: SkillField) =>
    serverErrors[field] ?? (form[field] ? clientErrors[field] : undefined);

  // A 422 lands under its field; anything else is a toast
  const showError = (error: unknown, fallback: string) => {
    const field =
      error instanceof SkillWriteError
        ? SKILL_WRITE_PROBLEM_FIELD[error.problem]
        : undefined;
    if (field && error instanceof SkillWriteError) {
      setServerErrors((current) => ({ ...current, [field]: error.message }));
      return;
    }
    toast.error(fallback);
  };

  const save = () => {
    putSkill.mutate(
      { ...form, create: !skill, currentName: skill?.name },
      {
        onSuccess: (entry) => onSaved?.(entry.path),
        onError: (error) =>
          showError(error, 'Could not save this skill. Try again in a moment.'),
      }
    );
  };

  const refine = () => {
    refineSkill.mutate(form, {
      onSuccess: (refined) => {
        setForm((current) => ({ ...current, ...refined }));
        setBodyVersion((version) => version + 1);
        setServerErrors({});
      },
      onError: (error) =>
        showError(error, 'Could not refine this skill. Try again in a moment.'),
    });
  };

  const remove = () => {
    if (!skill) return;
    deleteSkill.mutate(skill.name, {
      onError: () =>
        toast.error('Could not delete this skill. Try again in a moment.'),
    });
  };

  return {
    form,
    bodyVersion,
    setField,
    errorFor,
    canSave,
    save,
    refine,
    canRefine,
    isRefining: refineSkill.isPending,
    remove,
    isDeleting: deleteSkill.isPending,
  };
}
